// Runtime assessment state held on a Change once it is accepted for assessment.
//
// This is the working record the assessor edits (Screen 5) and the frozen package
// approved in the assessment step. It reads its structure from the instantiated
// template snapshot, never from the live template, so a later template edit cannot
// silently change an assessment in progress or a signed baseline.

import type { TChangeAssessmentTemplateSnapshot, TConfirmationState, TFieldValue } from './assessmentTemplate';

// Where a stored value originated. Suggestions start Unconfirmed; request facts
// may start Confirmed from request.
export const ASSESSMENT_VALUE_SOURCE = {
  REQUEST: 'Request',
  TEMPLATE_DEFAULT: 'Template default',
  TEMPLATE_SUGGESTION: 'Template suggestion',
  ASSESSOR: 'Assessor',
} as const;
export type TAssessmentValueSource = (typeof ASSESSMENT_VALUE_SOURCE)[keyof typeof ASSESSMENT_VALUE_SOURCE];

// One stored value. Used for both instantiated custom fields and question answers;
// the two are kept in separate maps so they stay semantically distinct even though
// they share this shape.
export type TAssessmentFieldValue = {
  fieldId: string;
  fieldOrigin: 'platform' | 'template';
  value: TFieldValue;
  source: TAssessmentValueSource;
  confirmationState: TConfirmationState;
  lastUpdatedBy: string;
  lastUpdatedAt: string;
  /** Rationale captured when the configuration requires one for this answer. */
  rationale?: string;
  /** Optional free-text comment (e.g. multi-select plus comment). */
  comment?: string;
};

// Section completion status shown in the navigator and used for gating submission.
export const SECTION_STATUS = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  NEEDS_INPUT: 'Needs input',
  COMPLETE: 'Complete',
} as const;
export type TSectionStatus = (typeof SECTION_STATUS)[keyof typeof SECTION_STATUS];

export type TSectionProgress = {
  sectionId: string;
  status: TSectionStatus;
  completedBy?: string;
  completedAt?: string;
};

// The assessor's decision on a candidate affected object.
export const AFFECTED_OBJECT_DECISION = {
  AFFECTED: 'Affected',
  REVIEW_REQUIRED: 'Review required',
  NOT_AFFECTED: 'Not affected',
  NOT_ASSESSED: 'Not assessed',
} as const;
export type TAffectedObjectDecision = (typeof AFFECTED_OBJECT_DECISION)[keyof typeof AFFECTED_OBJECT_DECISION];

export type TAffectedObjectState = {
  objectId: string;
  decision: TAffectedObjectDecision;
  rationale?: string;
  responsibleFunction?: string;
};

// A required approver, expressed as a role and the reason it is required. Roles,
// never hardcoded individuals.
export type TApproverRole = {
  role: string;
  reason: string;
};

// A frozen submission version created when the assessment is submitted.
export type TAssessmentSubmission = {
  version: number;
  submittedBy: string;
  submittedAt: string;
  completedSections: number;
  confirmedAffectedObjects: number;
  unresolvedNonBlockingQuestions: number;
  approverRoles: TApproverRole[];
};

export const APPROVAL_DECISION = {
  APPROVE: 'Approve',
  APPROVE_WITH_CONDITIONS: 'Approve with conditions',
  RETURN: 'Return for revision',
  REJECT: 'Reject',
} as const;
export type TApprovalDecision = (typeof APPROVAL_DECISION)[keyof typeof APPROVAL_DECISION];

// A recorded electronic-signature approval (prototype reauthentication, no real service).
export type TApprovalRecord = {
  decision: TApprovalDecision;
  signedBy: string;
  role: string;
  meaning: string;
  comment?: string;
  signedAt: string;
  conditions?: string[];
};

// The immutable, signed assessment baseline created on assessment approval.
export type TSignedBaseline = {
  signedAt: string;
  signedBy: string;
  submissionVersion: number;
  meaning: string;
};

// -- Screen 6 decision package (adapted from the rich domain summary content).
export type TAssessmentKeyConclusion = {
  title: string;
  conclusion: string;
  status?: string;
};

export type TConfirmedScopeGroup = {
  area: string;
  impact: string;
  rationale: string;
  documents?: string[];
};

export type TAssessmentOpenItems = {
  blocking: string[];
  assumptions: string[];
  conditions: string[];
  externalDecisions: string[];
};

export type TAssessmentSummary = {
  justification: string;
  whatChanges: string[];
  whatUnchanged: string[];
  impactLevel: string;
  safetySummary: string;
  regulatorySummary: string;
  keyConclusions: TAssessmentKeyConclusion[];
  confirmedScope: TConfirmedScopeGroup[];
  openItems: TAssessmentOpenItems;
};

// A draft planning item generated from an accepted preliminary activity after
// assessment approval. Not an active task - planning owns real tasks.
export type TDraftPlanningItem = {
  id: string;
  title: string;
  responsibleFunction: string;
  owner: string;
  dueInDays: number;
  status: 'Draft';
};

// The full live assessment record attached to a Change.
export type TChangeAssessment = {
  templateId: string;
  templateVersion: number;
  snapshot: TChangeAssessmentTemplateSnapshot;
  /** Instantiated template custom-field values, keyed by field id. */
  customFieldValues: Record<string, TAssessmentFieldValue>;
  /** Assessment question answers, keyed by question id. */
  answers: Record<string, TAssessmentFieldValue>;
  /** Section completion state, keyed by section id. */
  sectionProgress: Record<string, TSectionProgress>;
  /** Assessor decisions on candidate affected objects. */
  affectedObjects: TAffectedObjectState[];
  /** Values preserved for fields whose visibility condition became false. */
  hiddenValues: Record<string, TFieldValue>;
  submission?: TAssessmentSubmission;
  approval?: TApprovalRecord;
  baseline?: TSignedBaseline;
  draftPlanningItems?: TDraftPlanningItem[];
  summary?: TAssessmentSummary;
};
